import { Search, X } from "lucide-react";
import type { ListState } from "./useListState";

export type FilterDef = { key: string; label: string; options: { value: string; label: string }[] };

type Props = {
  list: ListState;
  filters: FilterDef[];
  /** Omit to hide the search box (when the page already has one). */
  searchLabel?: string;
  /** `dir` makes an option set both the field and the direction (e.g. Most / Least). */
  sortOptions?: { key: string; label: string; dir?: "asc" | "desc" }[];
};

/** Search + filter chips + sort. All state lives in the URL through `list`. */
export function FilterBar({ list, filters, searchLabel, sortOptions }: Props) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {searchLabel && <div className="relative min-w-0 flex-1 basis-56">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <input
            type="search"
            aria-label={searchLabel}
            placeholder={searchLabel}
            value={list.q}
            onChange={(e) => list.setQ(e.target.value)}
            className="field w-full rounded-control py-2.5 pl-9 pr-3 text-base sm:text-sm"
          />
        </div>}
        {sortOptions && (
          <select
            aria-label="Sort by"
            value={`${list.sortKey}|${list.sortDir}`}
            onChange={(e) => {
              const [k, d] = e.target.value.split("|");
              list.setSort(k, d as "asc" | "desc");
            }}
            className="field rounded-control px-3 py-2.5 text-base sm:text-sm"
          >
            {sortOptions.map((o) => (
              <option key={`${o.key}|${o.dir ?? list.sortDir}`} value={`${o.key}|${o.dir ?? list.sortDir}`}>
                {o.label}
              </option>
            ))}
          </select>
        )}
        {list.activeCount > 0 && (
          <button
            type="button"
            onClick={list.clear}
            className="inline-flex items-center gap-1 rounded-control px-3 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <X size={14} /> Clear
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {filters.map((f) => (
          <div key={f.key} role="group" aria-label={f.label} className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-semibold text-muted-foreground">{f.label}</span>
            {[{ value: "", label: "All" }, ...f.options].map((o) => {
              const active = (list.filters[f.key] ?? "") === o.value;
              return (
                <button
                  key={o.value || "all"}
                  type="button"
                  aria-pressed={active}
                  onClick={() => list.setFilter(f.key, o.value)}
                  className={`min-h-8 rounded-full border px-3 text-xs font-semibold transition focus-visible:ring-2 ring-ring ${
                    active
                      ? "border-primary bg-accent-soft text-primary"
                      : "border-border bg-card text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
